import config from "../config";

const AUTH_TOKEN_KEY = "authToken";

export const getStoredToken = () => {
  try {
    const raw = localStorage.getItem(AUTH_TOKEN_KEY) || sessionStorage.getItem(AUTH_TOKEN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.token || null;
  } catch {
    return null;
  }
};

export const getStoredUserRole = () => {
  try {
    const raw = localStorage.getItem(AUTH_TOKEN_KEY) || sessionStorage.getItem(AUTH_TOKEN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.user?.role?.toString().toLowerCase() || null;
  } catch {
    return null;
  }
};

export const getStoredUserId = () => {
  try {
    const raw = localStorage.getItem(AUTH_TOKEN_KEY) || sessionStorage.getItem(AUTH_TOKEN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.user?.id || parsed?.user?.email || null;
  } catch {
    return null;
  }
};

export const getNotificationStorageKey = () => {
  const userId = getStoredUserId();
  return userId ? `notifications_${userId}` : 'allNotifications';
};

export const getUnreadCountStorageKey = () => {
  const userId = getStoredUserId();
  return userId ? `unreadCount_${userId}` : 'unreadNotificationCount';
};

export const getKnownItemsStorageKey = (category) => {
  const userId = getStoredUserId();
  return userId ? `known_${category}_${userId}` : `known_${category}`;
};

export const getAuthHeaders = () => {
  const token = getStoredToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ---------------------------------------------------------------------------
// Request cache — deduplicates simultaneous GET calls and caches responses
// for 30 seconds so rapid sequential fetches (e.g. page navigation, multiple
// widgets mounting at once) reuse the same response without extra HTTP round-trips.
//
// Structure per entry:
//   { promise: Promise<Response>, timestamp: number, settled: boolean }
//
// "settled" = false while the fetch is still in-flight (dedup window).
// "settled" = true once the fetch completed (cache window = CACHE_TTL).
// The stored Response is never body-consumed here; callers always get .clone().
// ---------------------------------------------------------------------------
const CACHE_TTL = 30_000; // 30 seconds
const _getCache = new Map();

/** Remove all cached GET responses whose URLs contain `urlFragment`. */
export const invalidateCache = (urlFragment) => {
  for (const key of _getCache.keys()) {
    if (!urlFragment || key.includes(urlFragment)) {
      _getCache.delete(key);
    }
  }
};

/** Remove every cached GET response (e.g. on logout). */
export const clearCache = () => _getCache.clear();

const _handleUnauthorized = () => {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  sessionStorage.removeItem(AUTH_TOKEN_KEY);
  window.dispatchEvent(new CustomEvent('auth_unauthorized'));
};

/**
 * Wrapper around fetch that automatically:
 * - prepends API_BASE_URL for relative paths ("/api/..."),
 * - attaches Authorization header when a token exists,
 * - handles 401 Unauthorized by dispatching a logout event,
 * - deduplicates simultaneous GET requests to the same URL,
 * - caches GET responses for CACHE_TTL ms to reduce server load.
 */
export const apiFetch = async (url, options = {}) => {
  const finalUrl = url?.startsWith("/") ? `${config.API_BASE_URL}${url}` : url;
  const method = (options.method || 'GET').toUpperCase();

  const headers = { ...(options.headers || {}), ...getAuthHeaders() };
  if (!(options.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }

  // ── GET: dedup + cache ────────────────────────────────────────────────────
  if (method === 'GET') {
    const existing = _getCache.get(finalUrl);

    if (existing) {
      const isInflight = !existing.settled;
      const isFresh = existing.settled && (Date.now() - existing.timestamp < CACHE_TTL);

      if (isInflight || isFresh) {
        try {
          const response = await existing.promise;
          return response.clone();
        } catch {
          // The in-flight request failed; fall through to retry.
          _getCache.delete(finalUrl);
        }
      } else {
        // Stale — evict and re-fetch.
        _getCache.delete(finalUrl);
      }
    }

    // No usable cache entry — fire the real request.
    const entry = { promise: null, timestamp: Date.now(), settled: false };

    entry.promise = fetch(finalUrl, { ...options, headers })
      .then((response) => {
        entry.settled = true;
        entry.timestamp = Date.now();

        if (response.status === 401) {
          _handleUnauthorized();
          _getCache.delete(finalUrl); // Never cache auth failures.
        }

        return response;
      })
      .catch((err) => {
        _getCache.delete(finalUrl);
        throw err;
      });

    _getCache.set(finalUrl, entry);

    try {
      const response = await entry.promise;
      return response.clone();
    } catch (error) {
      throw error;
    }
  }

  // ── Non-GET: always pass through; invalidate related cache entries ────────
  try {
    const response = await fetch(finalUrl, { ...options, headers });

    if (response.status === 401) {
      _handleUnauthorized();
    }

    // Mutating a resource means any cached GET for the same base path is stale.
    // Strip query params to get the base resource URL for invalidation.
    const baseUrl = finalUrl.split('?')[0];
    invalidateCache(baseUrl);

    return response;
  } catch (error) {
    throw error;
  }
};

export const logUserActivity = async (action, details = "", metadata = {}) => {
  try {
    await apiFetch("/api/activities", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action, details, metadata }),
    });
  } catch (err) {
    // Activity logging is best-effort; failures are non-critical
  }
};
